import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '../ui/button'
import { Mail, RefreshCw, CheckCircle, AlertCircle, LogOut, Loader2, Sparkles } from 'lucide-react'
import { authAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface EmailVerificationOverlayProps {
  userEmail: string
  onLogout: () => void
  onVerificationComplete: () => void
  isVerified?: boolean // New prop to trigger success animation
}

const EmailVerificationOverlay: React.FC<EmailVerificationOverlayProps> = ({
  userEmail,
  onLogout,
  // onVerificationComplete is handled by the parent component via polling
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onVerificationComplete: _onVerificationComplete,
  isVerified = false,
}) => {
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const { toast } = useToast()
  const overlayRef = useRef<HTMLDivElement>(null)
  const firstFocusableRef = useRef<HTMLButtonElement>(null)
  const lastFocusableRef = useRef<HTMLButtonElement>(null)

  // Trigger entrance animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50)
    return () => clearTimeout(timer)
  }, [])

  // Handle verification success animation
  useEffect(() => {
    if (isVerified) {
      setShowSuccessAnimation(true)
    }
  }, [isVerified])

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [cooldownSeconds])

  // Focus trap implementation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Prevent escape from dismissing
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    // Focus trap for Tab key
    if (e.key === 'Tab') {
      const focusableElements = overlayRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      
      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }
  }, [])

  // Set up focus trap and prevent background interactions
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    
    // Focus the first focusable element when overlay mounts
    const timer = setTimeout(() => {
      firstFocusableRef.current?.focus()
    }, 100)

    // Prevent scrolling on the body
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
      clearTimeout(timer)
    }
  }, [handleKeyDown])

  const handleResendEmail = async () => {
    if (cooldownSeconds > 0 || isResending) return

    setIsResending(true)
    setResendError(null)
    setResendSuccess(false)

    try {
      await authAPI.resendConfirmation(userEmail)
      setResendSuccess(true)
      setCooldownSeconds(60) // 60 second cooldown
      toast({
        title: "Verification email sent!",
        description: "Please check your inbox and spam folder.",
      })
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to send verification email. Please try again."
      setResendError(errorMessage)
      toast({
        title: "Failed to send email",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsResending(false)
    }
  }

  const handleLogout = () => {
    onLogout()
  }

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-xl transition-all duration-500 ease-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${showSuccessAnimation ? 'bg-green-50/90' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="verification-title"
      aria-describedby="verification-description"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Success Confetti/Sparkle Effect */}
      {showSuccessAnimation && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10%',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <Sparkles className={`w-4 h-4 ${
                i % 3 === 0 ? 'text-[#D64463]' : i % 3 === 1 ? 'text-[#9C27B0]' : 'text-[#DEA319]'
              }`} />
            </div>
          ))}
        </div>
      )}

      {/* Organic Blob Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-left blob - Pink/Purple gradient - Hidden on very small screens */}
        <svg
          className={`absolute -top-20 -left-20 w-[250px] h-[250px] sm:w-[350px] sm:h-[350px] md:w-[400px] md:h-[400px] opacity-30 transition-all duration-1000 ${
            isVisible ? 'translate-x-0 translate-y-0' : '-translate-x-20 -translate-y-20'
          }`}
          style={{ animation: 'blob-float-1 15s ease-in-out infinite' }}
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="blob-gradient-1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D64463" />
              <stop offset="100%" stopColor="#9C27B0" />
            </linearGradient>
          </defs>
          <path
            fill="url(#blob-gradient-1)"
            d="M44.1,-76.3C58.9,-69.9,73.8,-60.8,81.3,-47.5C88.8,-34.2,88.9,-17.1,85.9,-1.7C82.9,13.6,76.8,27.2,68.4,39.2C60,51.2,49.3,61.6,37,68.8C24.7,76,12.3,80,0,80C-12.4,80,-24.8,76,-36.2,69.7C-47.6,63.4,-58,54.8,-65.8,43.8C-73.6,32.8,-78.8,19.4,-81.2,5.1C-83.6,-9.2,-83.2,-24.4,-77.4,-37.2C-71.6,-50,-60.4,-60.4,-47.3,-67.7C-34.2,-75,-17.1,-79.2,-0.6,-78.2C15.9,-77.2,31.8,-71,44.1,-76.3Z"
            transform="translate(100 100)"
          />
        </svg>

        {/* Bottom-right blob - Purple/Pink gradient */}
        <svg
          className={`absolute -bottom-20 -right-20 w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] md:w-[450px] md:h-[450px] opacity-25 transition-all duration-1000 delay-200 ${
            isVisible ? 'translate-x-0 translate-y-0' : 'translate-x-20 translate-y-20'
          }`}
          style={{ animation: 'blob-float-2 18s ease-in-out infinite' }}
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="blob-gradient-2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#9C27B0" />
              <stop offset="100%" stopColor="#D64463" />
            </linearGradient>
          </defs>
          <path
            fill="url(#blob-gradient-2)"
            d="M39.5,-67.5C52.9,-60.5,66.8,-53.3,74.8,-41.8C82.8,-30.3,84.9,-15.2,83.7,-0.7C82.5,13.8,78,27.5,70.3,39.3C62.6,51.1,51.7,60.9,39.1,67.5C26.5,74.1,13.3,77.5,-0.5,78.4C-14.3,79.3,-28.6,77.7,-41.2,71.4C-53.8,65.1,-64.7,54.1,-72.1,41.2C-79.5,28.3,-83.4,14.2,-83.1,0.2C-82.8,-13.8,-78.3,-27.6,-70.6,-39.3C-62.9,-51,-52,-60.6,-39.5,-68C-27,-75.4,-13.5,-80.6,0.1,-80.8C13.7,-81,27.4,-76.2,39.5,-67.5Z"
            transform="translate(100 100)"
          />
        </svg>

        {/* Top-right blob - Amber/Gold accent - Hidden on mobile */}
        <svg
          className={`absolute -top-10 -right-10 w-[200px] h-[200px] sm:w-[250px] sm:h-[250px] md:w-[300px] md:h-[300px] opacity-20 transition-all duration-1000 delay-300 hidden sm:block ${
            isVisible ? 'translate-x-0 translate-y-0' : 'translate-x-10 -translate-y-10'
          }`}
          style={{ animation: 'blob-float-3 12s ease-in-out infinite' }}
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="blob-gradient-3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DEA319" />
              <stop offset="100%" stopColor="#D64463" />
            </linearGradient>
          </defs>
          <path
            fill="url(#blob-gradient-3)"
            d="M47.7,-79.1C62.3,-71.8,75.1,-60.3,82.4,-45.9C89.7,-31.5,91.5,-14.2,89.1,2.1C86.7,18.4,80.1,33.8,70.5,47.1C60.9,60.4,48.3,71.6,33.8,78.1C19.3,84.6,2.9,86.4,-13.1,84.1C-29.1,81.8,-44.7,75.4,-57.4,65.1C-70.1,54.8,-79.9,40.6,-84.4,24.8C-88.9,9,-88.1,-8.4,-83.1,-24.1C-78.1,-39.8,-68.9,-53.8,-56.3,-62.1C-43.7,-70.4,-27.7,-73,-12.4,-75.8C2.9,-78.6,18.1,-81.6,33.1,-86.4C48.1,-91.2,62.9,-97.8,47.7,-79.1Z"
            transform="translate(100 100)"
          />
        </svg>

        {/* Bottom-left blob - Subtle pink - Hidden on mobile */}
        <svg
          className={`absolute -bottom-10 -left-10 w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] md:w-[350px] md:h-[350px] opacity-15 transition-all duration-1000 delay-400 hidden sm:block ${
            isVisible ? 'translate-x-0 translate-y-0' : '-translate-x-10 translate-y-10'
          }`}
          style={{ animation: 'blob-float-4 20s ease-in-out infinite' }}
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="blob-gradient-4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D64463" />
              <stop offset="50%" stopColor="#9C27B0" />
              <stop offset="100%" stopColor="#DEA319" />
            </linearGradient>
          </defs>
          <path
            fill="url(#blob-gradient-4)"
            d="M42.8,-73.2C55.9,-66.5,67.1,-55.8,74.6,-42.8C82.1,-29.8,85.9,-14.9,85.5,-0.2C85.1,14.5,80.5,29,72.4,41.5C64.3,54,52.7,64.5,39.4,71.3C26.1,78.1,11.1,81.2,-3.5,80.1C-18.1,79,-36.2,73.7,-50.1,64.1C-64,54.5,-73.7,40.6,-79.1,25.1C-84.5,9.6,-85.6,-7.5,-81.4,-22.8C-77.2,-38.1,-67.7,-51.6,-55.1,-58.7C-42.5,-65.8,-26.8,-66.5,-12.3,-68.9C2.2,-71.3,16.4,-75.4,29.7,-79.9C43,-84.4,55.4,-89.3,42.8,-73.2Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>

      {/* Main Card */}
      <div className={`relative z-10 w-full max-w-md mx-3 sm:mx-4 bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
      } ${showSuccessAnimation ? 'ring-4 ring-green-400/50 shadow-green-200/50' : ''}`}>
        {/* Card Content */}
        <div className="p-6 sm:p-8 md:p-10">
          {/* Mail Icon/Illustration */}
          <div className={`flex justify-center mb-4 sm:mb-6 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`} style={{ transitionDelay: '200ms' }}>
            <div className="relative">
              {showSuccessAnimation ? (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center animate-success-bounce">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-300/50">
                    <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-white animate-success-check" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-[#D64463]/10 to-[#9C27B0]/10 flex items-center justify-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#D64463] to-[#9C27B0] flex items-center justify-center shadow-lg animate-icon-pulse">
                      <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                  </div>
                  {/* Decorative ring */}
                  <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#D64463]/30" style={{ animation: 'spin 20s linear infinite' }} />
                </>
              )}
            </div>
          </div>

          {/* Heading */}
          <h1
            id="verification-title"
            className={`text-xl sm:text-2xl md:text-3xl font-bold text-center mb-2 sm:mb-3 transition-all duration-500 ${
              showSuccessAnimation ? 'text-green-700' : 'text-[#1F303B]'
            } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '300ms' }}
          >
            {showSuccessAnimation ? 'Email Verified!' : 'Verify Your Email Address'}
          </h1>

          {/* Description */}
          <p
            id="verification-description"
            className={`text-center text-gray-500 text-sm sm:text-base mb-4 sm:mb-6 transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            {showSuccessAnimation 
              ? 'Welcome to CareVault! Redirecting you now...'
              : 'We sent a verification email to:'
            }
          </p>

          {/* Email Display */}
          {!showSuccessAnimation && (
            <div className={`bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 border border-gray-100 transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`} style={{ transitionDelay: '500ms' }}>
              <p className="text-center font-semibold text-[#1F303B] break-all text-sm sm:text-base">
                {userEmail}
              </p>
            </div>
          )}

          {/* Explanatory Text */}
          {!showSuccessAnimation && (
            <p className={`text-xs sm:text-sm text-center text-gray-500 mb-6 sm:mb-8 transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`} style={{ transitionDelay: '600ms' }}>
              Please check your inbox and spam folder. Click the verification link to activate your account and access all CareVault features.
            </p>
          )}

          {/* Success Message */}
          {resendSuccess && !showSuccessAnimation && (
            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-green-50 rounded-xl sm:rounded-2xl border border-green-200 mb-4 sm:mb-6 animate-slide-in-from-top">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-xs sm:text-sm font-medium text-green-800">Email sent successfully!</p>
                <p className="text-[10px] sm:text-xs text-green-600">Check your inbox and spam folder</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {resendError && !showSuccessAnimation && (
            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-red-50 rounded-xl sm:rounded-2xl border border-red-200 mb-4 sm:mb-6 animate-slide-in-from-top">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-xs sm:text-sm font-medium text-red-800">Failed to send email</p>
                <p className="text-[10px] sm:text-xs text-red-600">{resendError}</p>
              </div>
            </div>
          )}

          {/* Resend Button */}
          {!showSuccessAnimation && (
            <Button
              ref={firstFocusableRef}
              onClick={handleResendEmail}
              disabled={isResending || cooldownSeconds > 0}
              className={`w-full h-12 sm:h-14 bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90 text-white text-base sm:text-lg font-semibold rounded-xl sm:rounded-2xl shadow-lg shadow-[#D64463]/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '700ms' }}
            >
              {isResending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  Sending...
                </span>
              ) : cooldownSeconds > 0 ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                  Resend in {cooldownSeconds}s
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                  Resend Verification Email
                </span>
              )}
            </Button>
          )}

          {/* Divider */}
          {!showSuccessAnimation && (
            <div className={`relative my-4 sm:my-6 transition-all duration-500 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`} style={{ transitionDelay: '800ms' }}>
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-gray-400">or</span>
              </div>
            </div>
          )}

          {/* Logout Button */}
          {!showSuccessAnimation && (
            <Button
              ref={lastFocusableRef}
              onClick={handleLogout}
              variant="outline"
              className={`w-full h-10 sm:h-12 border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-xl sm:rounded-2xl transition-all duration-300 text-sm sm:text-base ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '900ms' }}
            >
              <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
              Log out and try later
            </Button>
          )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        /* Blob floating animations - each blob has unique movement */
        @keyframes blob-float-1 {
          0%, 100% {
            transform: translate(0, 0) scale(1) rotate(0deg);
          }
          25% {
            transform: translate(30px, -25px) scale(1.05) rotate(5deg);
          }
          50% {
            transform: translate(-15px, 30px) scale(0.95) rotate(-3deg);
          }
          75% {
            transform: translate(-25px, -15px) scale(1.02) rotate(2deg);
          }
        }
        
        @keyframes blob-float-2 {
          0%, 100% {
            transform: translate(0, 0) scale(1) rotate(0deg);
          }
          33% {
            transform: translate(-25px, 20px) scale(1.03) rotate(-4deg);
          }
          66% {
            transform: translate(20px, -30px) scale(0.97) rotate(3deg);
          }
        }
        
        @keyframes blob-float-3 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-20px, 25px) scale(1.08);
          }
        }
        
        @keyframes blob-float-4 {
          0%, 100% {
            transform: translate(0, 0) scale(1) rotate(0deg);
          }
          20% {
            transform: translate(15px, -20px) scale(1.04) rotate(2deg);
          }
          40% {
            transform: translate(-10px, 15px) scale(0.96) rotate(-2deg);
          }
          60% {
            transform: translate(20px, 10px) scale(1.02) rotate(1deg);
          }
          80% {
            transform: translate(-15px, -10px) scale(0.98) rotate(-1deg);
          }
        }
        
        /* Icon pulse animation */
        @keyframes icon-pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(214, 68, 99, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 15px 25px -5px rgba(214, 68, 99, 0.4);
          }
        }
        
        .animate-icon-pulse {
          animation: icon-pulse 2s ease-in-out infinite;
        }
        
        /* Spin animation for decorative ring */
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        /* Success animations */
        @keyframes success-bounce {
          0%, 100% {
            transform: scale(1);
          }
          25% {
            transform: scale(1.1);
          }
          50% {
            transform: scale(0.95);
          }
          75% {
            transform: scale(1.05);
          }
        }
        
        .animate-success-bounce {
          animation: success-bounce 0.6s ease-out;
        }
        
        @keyframes success-check {
          0% {
            transform: scale(0) rotate(-45deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.2) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        
        .animate-success-check {
          animation: success-check 0.5s ease-out 0.2s both;
        }
        
        /* Confetti animation */
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
        
        /* Slide in from top animation */
        @keyframes slide-in-from-top {
          0% {
            transform: translateY(-10px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-from-top {
          animation: slide-in-from-top 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}

export default EmailVerificationOverlay
